import React, { Component } from 'react';
import { Shield, FileSearch, AlertCircle, CheckCircle, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
export const ToxicityAuditSection = () => {
  return <section className="py-24 md:py-32 bg-[#0a0a0a] relative overflow-hidden" data-id="element-3127">
      {/* Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] opacity-50 pointer-events-none" data-id="element-3128" />

      {/* Scanning Line Animation */}
      <motion.div animate={{
      y: ['0%', '100%'],
      opacity: [0, 1, 1, 0]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'linear'
    }} className="absolute left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FF0000] to-transparent shadow-[0_0_20px_#FF0000]" data-id="element-3129" />

      <div className="max-w-6xl mx-auto px-4 md:px-6 relative z-10" data-id="element-3130">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16 md:mb-20" data-id="element-3131">
          <div className="inline-flex items-center gap-2 mb-6 bg-[#FFD700]/10 border border-[#FFD700]/30 px-4 py-2 rounded-full" data-id="element-3132">
            <Shield size={16} className="text-[#FFD700]" data-id="element-3133" />
            <span className="text-[#FFD700] font-mono text-xs tracking-widest uppercase font-bold" data-id="element-3134">
              Diagnostic Protocol
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 uppercase tracking-tight leading-none px-4" data-id="element-3135">
            The <span className="text-[#FFD700]" data-id="element-3136">Toxicity Audit</span>
          </h2>

          <p className="text-base md:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed font-mono mb-4 px-4" data-id="element-3137">
            This isn't a "fun quiz." It's a diagnostic tool for survival.
          </p>

          <div className="inline-flex items-center gap-2 text-sm text-gray-500 font-mono" data-id="element-3138">
            <span className="w-2 h-2 bg-[#FF0000] rounded-full animate-pulse" data-id="element-3139" />
            <span data-id="element-3140">CLINICAL-GRADE ANALYSIS</span>
          </div>
        </motion.div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 mb-12 md:mb-16" data-id="element-3141">
          {/* Left: What It Is */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-3142">
            <div className="relative bg-[#050505] border-2 border-[#FFD700]/30 p-6 md:p-8 h-full" data-id="element-3143">
              {/* Corner Accents */}
              <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-[#FFD700]" data-id="element-3144" />
              <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-[#FFD700]" data-id="element-3145" />
              <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-[#FFD700]" data-id="element-3146" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-[#FFD700]" data-id="element-3147" />

              <div className="flex items-center gap-3 mb-8" data-id="element-3148">
                <div className="w-14 h-14 bg-[#FFD700] flex items-center justify-center flex-shrink-0" data-id="element-3149">
                  <FileSearch size={28} className="text-black" data-id="element-3150" />
                </div>
                <div className="min-w-0" data-id="element-3151">
                  <div className="text-[10px] font-mono text-gray-500 tracking-widest uppercase" data-id="element-3152">
                    System Function
                  </div>
                  <h3 className="text-xl md:text-2xl font-black text-white uppercase tracking-tight" data-id="element-3153">
                    What It Does
                  </h3>
                </div>
              </div>

              <div className="space-y-6" data-id="element-3154">
                <p className="text-gray-300 text-base md:text-lg leading-relaxed" data-id="element-3155">
                  The Toxicity Audit uses forensic AI to analyze conversation
                  patterns for{' '}
                  <span className="text-white font-bold" data-id="element-3156">
                    manipulation tactics
                  </span>{' '}
                  your brain has been trained to ignore.
                </p>

                <div className="space-y-4" data-id="element-3157">
                  <AuditFeature icon={<CheckCircle size={18} className="text-[#FFD700]" data-id="element-3159" />} text="Detects gaslighting, DARVO, and covert narcissism" data-id="element-3158" />
                  <AuditFeature icon={<CheckCircle size={18} className="text-[#FFD700]" data-id="element-3161" />} text="Maps emotional drainage patterns over time" data-id="element-3160" />
                  <AuditFeature icon={<CheckCircle size={18} className="text-[#FFD700]" data-id="element-3163" />} text="Identifies intermittent reinforcement cycles" data-id="element-3162" />
                  <AuditFeature icon={<CheckCircle size={18} className="text-[#FFD700]" data-id="element-3165" />} text="Generates evidence-based severity scores" data-id="element-3164" />
                </div>

                <div className="bg-[#FFD700]/10 border border-[#FFD700]/30 p-4 rounded" data-id="element-3166">
                  <div className="text-xs font-mono text-[#FFD700] mb-2 uppercase tracking-widest" data-id="element-3167">
                    ⚡ Critical Function
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed font-mono" data-id="element-3168">
                    This tool sees what you can't:{' '}
                    <span className="text-white font-bold" data-id="element-3169">
                      the systematic patterns
                    </span>{' '}
                    hidden beneath "normal" conflicts.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: Why You Need It */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} data-id="element-3170">
            <div className="relative bg-[#050505] border-2 border-[#FF0000]/30 p-6 md:p-8 h-full" data-id="element-3171">
              {/* Corner Accents */}
              <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-[#FF0000]" data-id="element-3172" />
              <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-[#FF0000]" data-id="element-3173" />
              <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-[#FF0000]" data-id="element-3174" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-[#FF0000]" data-id="element-3175" />

              <div className="flex items-center gap-3 mb-8" data-id="element-3176">
                <div className="w-14 h-14 bg-[#FF0000] flex items-center justify-center flex-shrink-0" data-id="element-3177">
                  <AlertCircle size={28} className="text-white" data-id="element-3178" />
                </div>
                <div className="min-w-0" data-id="element-3179">
                  <div className="text-[10px] font-mono text-gray-500 tracking-widest uppercase" data-id="element-3180">
                    Critical Need
                  </div>
                  <h3 className="text-xl md:text-2xl font-black text-white uppercase tracking-tight" data-id="element-3181">
                    Why It Matters
                  </h3>
                </div>
              </div>

              <div className="space-y-6" data-id="element-3182">
                <div data-id="element-3183">
                  <h4 className="text-base md:text-lg font-bold text-[#FF0000] mb-4 uppercase tracking-wide" data-id="element-3184">
                    Without External Analysis:
                  </h4>
                  <ul className="space-y-3" data-id="element-3185">
                    <ThreatPoint text="You remain trapped in cognitive dissonance" data-id="element-3186" />
                    <ThreatPoint text="Gaslighting continues to erode your reality" data-id="element-3187" />
                    <ThreatPoint text="You blame yourself for 'not trying hard enough'" data-id="element-3188" />
                    <ThreatPoint text="The emotional parasite feeds undetected" data-id="element-3189" />
                  </ul>
                </div>

                <div className="h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent" data-id="element-3190" />

                <div data-id="element-3191">
                  <h4 className="text-base md:text-lg font-bold text-[#FFD700] mb-4 uppercase tracking-wide" data-id="element-3192">
                    With The Audit:
                  </h4>
                  <ul className="space-y-3" data-id="element-3193">
                    <SolutionPoint text="Objective data validates your experience" data-id="element-3194" />
                    <SolutionPoint text="Pattern recognition breaks denial" data-id="element-3195" />
                    <SolutionPoint text="Evidence empowers informed decisions" data-id="element-3196" />
                    <SolutionPoint text="Reality anchoring prevents further manipulation" data-id="element-3197" />
                  </ul>
                </div>

                <div className="bg-[#FF0000]/10 border border-[#FF0000]/30 p-4 rounded" data-id="element-3198">
                  <div className="text-xs font-mono text-[#FF0000] mb-2 uppercase tracking-widest flex items-center gap-2" data-id="element-3199">
                    <span className="w-2 h-2 bg-[#FF0000] rounded-full animate-pulse flex-shrink-0" data-id="element-3200" />
                    Survival Imperative
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed font-mono" data-id="element-3201">
                    You can't fix what you can't see. This audit makes the
                    invisible visible.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6,
        delay: 0.6
      }} className="relative" data-id="element-3202">
          <div className="bg-gradient-to-r from-[#050505] via-[#0a0a0a] to-[#050505] border-2 border-[#FFD700] p-6 md:p-8 lg:p-12 text-center rounded" data-id="element-3203">
            {/* Scanning Line */}
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FFD700] to-transparent animate-[scan_3s_ease-in-out_infinite]" data-id="element-3204" />

            <div className="max-w-3xl mx-auto" data-id="element-3205">
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-4 uppercase tracking-tight leading-tight" data-id="element-3206">
                Stop Guessing.
                <br data-id="element-3207" />
                <span className="text-[#FFD700]" data-id="element-3208">Start Knowing.</span>
              </h3>

              <p className="text-gray-300 text-base md:text-lg mb-8 leading-relaxed px-4" data-id="element-3209">
                The Toxicity Audit isn't entertainment—it's a diagnostic
                protocol designed to detect patterns your nervous system has
                learned to ignore.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8" data-id="element-3210">
                <a href="/signup" className="group relative px-8 py-4 bg-[#FFD700] text-black font-bold text-base md:text-lg uppercase tracking-wider overflow-hidden hover:bg-[#FFD700]/90 transition-all shadow-[0_0_20px_rgba(255,215,0,0.4)] hover:shadow-[0_0_30px_rgba(255,215,0,0.6)] w-full sm:w-auto min-w-[280px] rounded" data-id="element-3211">
                  <span className="relative z-10 inline-flex items-center justify-center gap-2 group-hover:scale-105 transition-transform duration-200" data-id="element-3212">
                    Run Diagnostic Scan
                    <ArrowRight size={20} data-id="element-3213" />
                  </span>
                </a>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-xs text-gray-500 font-mono" data-id="element-3214">
                <div className="flex items-center gap-2" data-id="element-3215">
                  <Shield size={14} className="text-[#FFD700]" data-id="element-3216" />
                  <span data-id="element-3217">ENCRYPTED</span>
                </div>
                <div className="w-px h-3 bg-gray-800 hidden sm:block" data-id="element-3218" />
                <div className="flex items-center gap-2" data-id="element-3219">
                  <CheckCircle size={14} className="text-[#FFD700]" data-id="element-3220" />
                  <span data-id="element-3221">ANONYMOUS</span>
                </div>
                <div className="w-px h-3 bg-gray-800 hidden sm:block" data-id="element-3222" />
                <div className="flex items-center gap-2" data-id="element-3223">
                  <FileSearch size={14} className="text-[#FFD700]" data-id="element-3224" />
                  <span data-id="element-3225">EVIDENCE-BASED</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
};
// Helper Components
const AuditFeature = ({
  icon,
  text
}: {
  icon: React.ReactNode;
  text: string;
}) => <div className="flex items-start gap-3" data-id="element-3226">
    <div className="flex-shrink-0 mt-0.5" data-id="element-3227">{icon}</div>
    <span className="text-gray-300 text-sm leading-relaxed" data-id="element-3228">{text}</span>
  </div>;
const ThreatPoint = ({
  text
}: {
  text: string;
}) => <li className="flex items-start gap-3" data-id="element-3229">
    <span className="flex-shrink-0 w-6 h-6 bg-[#FF0000]/20 border border-[#FF0000]/50 flex items-center justify-center text-[#FF0000] font-bold text-xs" data-id="element-3230">
      ✕
    </span>
    <span className="text-gray-400 text-sm leading-relaxed pt-0.5" data-id="element-3231">{text}</span>
  </li>;
const SolutionPoint = ({
  text
}: {
  text: string;
}) => <li className="flex items-start gap-3" data-id="element-3232">
    <span className="flex-shrink-0 w-6 h-6 bg-[#FFD700]/20 border border-[#FFD700]/50 flex items-center justify-center text-[#FFD700] font-bold text-xs" data-id="element-3233">
      ✓
    </span>
    <span className="text-gray-300 text-sm leading-relaxed pt-0.5" data-id="element-3234">{text}</span>
  </li>;